import * as Sentry from '@sentry/react';
import { initSentry } from '@wavvest/shared-services';

// Environment detection using Vite's import.meta.env
const isProd = import.meta.env.PROD;

// DSN from environment variable
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initializeSentry() {
  // Mirror the original `!isProd && !import.meta.env.VITE_FORCE_SENTRY`
  // beforeSend semantics: any truthy value (string, "1", etc.) forces sending.
  initSentry({
    dsn: SENTRY_DSN,
    isProd,
    forceSentry: Boolean(import.meta.env.VITE_FORCE_SENTRY),
    release: `wavvest-client-portal@${import.meta.env.VITE_APP_VERSION || 'unknown'}`,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Set user context when authenticated
export function setSentryUser(user: { id?: string; username?: string }) {
  Sentry.setUser({
    id: user.id,
    username: user.username,
  });
}

// Clear user context on logout
export function clearSentryUser() {
  Sentry.setUser(null);
}

// Add custom tags
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// Capture exceptions with context
export function captureException(error: Error, context?: Record<string, unknown>): string {
  return Sentry.captureException(error, {
    extra: context,
  });
}

export function captureExceptionWithContext(
  error: Error,
  {
    tags,
    extra,
  }: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  } = {},
) {
  Sentry.withScope((scope) => {
    Object.entries(tags || {}).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

// Capture messages
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): string {
  return Sentry.captureMessage(message, level);
}

export function captureMessageWithContext(
  message: string,
  {
    level = "info",
    tags,
    extra,
  }: {
    level?: Sentry.SeverityLevel;
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  } = {},
) {
  Sentry.withScope((scope) => {
    Object.entries(tags || {}).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureMessage(message, level);
  });
}

// Re-export ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary;
